import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import type { SlotPage } from "@common/utils/redline/insertion-slot";

import type { InsertionLandings } from "../components/chat/proposal-card/insertion-landings-context";
import {
  isSlotBeingRanked,
  SLOT_PLACE_AT_ANCHOR,
  type SlotCard,
  type SlotCardReading,
  type SlotPlace,
  slotCardsOf,
  slotPlaceOf,
  slotRankingChangeCountNow,
  slotRanksOf,
  slotReadingsOf,
  sharedSlotsOf,
  subscribeToSlotRanking,
} from "../utils/redline-insertion-slot";
import type { ConversationMessage } from "../utils/web-api";

export interface InsertionSlotLandingsInput {
  conversationId: string | undefined;
  turns: ConversationMessage[];
  readingIsPaused: boolean;
  isActionApplied: (conversationId: string, actionId: string) => boolean;
  page: SlotPage;
  reportFailure: (failure: unknown) => void;
}

interface ConversationReadings {
  conversationId: string;
  readingsByCardAndAnchor: Map<string, SlotCardReading>;
}

const KEY_SEPARATOR = "\u0000";
const SLOT_SEPARATOR = "\u0001";

const readingKeyOf = (cardId: string, anchorBookmark: string): string => `${cardId}${KEY_SEPARATOR}${anchorBookmark}`;

function sharedAnchorsByCardIdOf(sharedSlots: Map<string, SlotCard[]>): Map<string, string[]> {
  const anchorsByCardId = new Map<string, string[]>();
  sharedSlots.forEach((cards, anchorBookmark) => {
    for (const card of cards) anchorsByCardId.set(card.cardId, [...(anchorsByCardId.get(card.cardId) ?? []), anchorBookmark]);
  });
  return anchorsByCardId;
}

export function useInsertionSlotLandings({ conversationId, turns, readingIsPaused, isActionApplied, page, reportFailure }: InsertionSlotLandingsInput): InsertionLandings {
  useSyncExternalStore(subscribeToSlotRanking, slotRankingChangeCountNow);
  const slotCards = useMemo(() => slotCardsOf(turns), [turns]);
  const sharedSlots = useMemo(() => sharedSlotsOf(slotCards), [slotCards]);
  const sharedAnchorsByCardId = useMemo(() => sharedAnchorsByCardIdOf(sharedSlots), [sharedSlots]);
  const ranks = conversationId === undefined ? new Map() : slotRanksOf(conversationId, turns);
  const anchorsBeingRanked = conversationId === undefined
    ? ""
    : Array.from(sharedSlots.keys()).filter((anchorBookmark) => isSlotBeingRanked(conversationId, anchorBookmark)).join(KEY_SEPARATOR);
  const pageStateKey = conversationId === undefined || readingIsPaused
    ? ""
    : Array.from(sharedSlots.entries())
      .map(([anchorBookmark, cards]) => [
        anchorBookmark,
        isSlotBeingRanked(conversationId, anchorBookmark) ? "ranking" : "settled",
        ...cards.map((card) => `${card.cardId}:${ranks.get(card.cardId)?.get(anchorBookmark)?.rank ?? "unranked"}:${isActionApplied(conversationId, card.cardId) ? "applied" : "open"}`),
      ].join(KEY_SEPARATOR))
      .join(SLOT_SEPARATOR);
  const [conversationReadings, setConversationReadings] = useState<ConversationReadings | null>(null);
  const currentReadings = conversationReadings !== null && conversationReadings.conversationId === conversationId ? conversationReadings.readingsByCardAndAnchor : null;
  const latestRef = useRef({ slotCards, sharedSlots, ranks, isActionApplied, reportFailure, currentReadings });
  latestRef.current = { slotCards, sharedSlots, ranks, isActionApplied, reportFailure, currentReadings };

  useEffect(() => {
    if (pageStateKey === "" || conversationId === undefined) return;
    const { sharedSlots: slotsToRead, ranks: ranksToRead, isActionApplied: isAppliedWhenRead, reportFailure: report } = latestRef.current;
    let cancelled = false;
    const readSlots = async (): Promise<void> => {
      const readingsByCardAndAnchor = new Map<string, SlotCardReading>();
      for (const [anchorBookmark, cards] of slotsToRead) {
        if (isSlotBeingRanked(conversationId, anchorBookmark)) continue;
        try {
          const readings = await slotReadingsOf({ anchorBookmark, cards, ranks: ranksToRead, isCardApplied: (cardId) => isAppliedWhenRead(conversationId, cardId), page });
          readings.forEach((reading, cardId) => readingsByCardAndAnchor.set(readingKeyOf(cardId, anchorBookmark), reading));
        } catch (failure) {
          if (!cancelled) report(failure);
        }
        if (cancelled) return;
      }
      setConversationReadings({ conversationId, readingsByCardAndAnchor });
    };
    void readSlots();
    return () => {
      cancelled = true;
    };
  }, [pageStateKey, conversationId, page]);

  const placeOf = useCallback(async (cardId: string, anchorBookmark: string): Promise<SlotPlace> => {
    const { slotCards: cardsNow, ranks: ranksNow, isActionApplied: isAppliedNow, reportFailure: report, currentReadings: readingsNow } = latestRef.current;
    const cardAddsAfterAnchor = cardsNow.some((card) => card.cardId === cardId && card.insertions.some((insertion) => insertion.anchorBookmark === anchorBookmark));
    if (conversationId === undefined || !cardAddsAfterAnchor) return SLOT_PLACE_AT_ANCHOR;
    const cardIsApplied = isAppliedNow(conversationId, cardId);
    if (!cardIsApplied && isSlotBeingRanked(conversationId, anchorBookmark)) return SLOT_PLACE_AT_ANCHOR;
    const reading = readingsNow?.get(readingKeyOf(cardId, anchorBookmark));
    if (reading !== undefined && reading.cardWasApplied === cardIsApplied) return reading.place;
    try {
      return await slotPlaceOf({ anchorBookmark, cardId, cardIsApplied, cards: cardsNow, ranks: ranksNow, page });
    } catch (failure) {
      report(failure);
      return SLOT_PLACE_AT_ANCHOR;
    }
  }, [conversationId, page]);

  return useMemo((): InsertionLandings => {
    const rankingAnchors = new Set(anchorsBeingRanked === "" ? [] : anchorsBeingRanked.split(KEY_SEPARATOR));
    return {
      landingOf: (cardId, anchorBookmark) => (rankingAnchors.has(anchorBookmark) ? null : currentReadings?.get(readingKeyOf(cardId, anchorBookmark))?.landing ?? null),
      isRankingSlotOf: (cardId) => (sharedAnchorsByCardId.get(cardId) ?? []).some((anchorBookmark) => rankingAnchors.has(anchorBookmark)),
      placeOf,
    };
  }, [anchorsBeingRanked, currentReadings, sharedAnchorsByCardId, placeOf]);
}
